import { useEffect } from 'react';
import { useLanguage } from '@/lib/i18n/LanguageContext';
import { useLocation } from 'react-router-dom';

interface SEOHeadProps {
  page?: 'home' | 'create' | 'auth' | 'my-parties' | 'guest' | 'admin' | 'thanks' | 'policy' | 'play-center';
  partyTitle?: string;
  childName?: string;
  playCenterName?: string;
  playCenterDescription?: string;
}

const BASE_URL = "https://partywhen.com";

const seoData = {
  en: {
    home: {
      title: "Free Kids Party Planner – Date Voting & RSVPs | PartyWhen",
      description: "Plan perfect kids birthday parties for free. Upload invites, vote on dates, manage RSVPs, wishlists & shopping lists. No accounts needed.",
    },
    create: {
      title: "Create Party Invitation | PartyWhen",
      description: "Create your child's birthday party invitation in minutes. Choose beautiful templates, set dates for voting, and share with guests easily.",
    },
    auth: {
      title: "Sign In | PartyWhen – Free Kids Party Planner",
      description: "Sign in to PartyWhen to save your parties, copy past events, and manage all your celebrations in one place.",
    },
    "my-parties": {
      title: "My Parties | PartyWhen – Free Kids Party Planner",
      description: "View and manage all your kids parties. Copy past events, track RSVPs, and access your admin dashboards.",
    },
    guest: {
      title: "You're Invited! | PartyWhen",
      description: "You've been invited to a party! Vote on your preferred dates, RSVP, and claim gift items from the wishlist.",
    },
    admin: {
      title: "Party Dashboard | PartyWhen – Manage Your Party",
      description: "Manage your party with real-time RSVPs, date voting results, guest dietary requirements, and more.",
    },
    thanks: {
      title: "Thank You Card | PartyWhen",
      description: "Create and share beautiful thank you cards for your party guests.",
    },
    policy: {
      title: "Cookie Policy & Disclaimer | PartyWhen",
      description: "Read our cookie policy and disclaimer. We only use essential cookies and accept no responsibility for user-generated content.",
    },
  },
  sv: {
    home: {
      title: "Gratis Barnkalas – Datumröstning & OSA | PartyWhen",
      description: "Planera perfekta barnkalas gratis. Ladda upp inbjudan, låt gäster rösta på datum, hantera OSA, önskelistor & inköpslistor. Inga konton behövs – dela bara en länk!",
    },
    create: {
      title: "Skapa Kalasinbjudan | Gratis Barnkalas Planerare | PartyWhen",
      description: "Skapa ditt barns kalasinbjudan på några minuter. Välj vackra mallar, sätt datum för röstning och dela enkelt med gäster.",
    },
    auth: {
      title: "Logga In | PartyWhen – Gratis Barnkalas Planerare",
      description: "Logga in på PartyWhen för att spara dina kalas, kopiera tidigare event och hantera alla dina firanden på ett ställe.",
    },
    "my-parties": {
      title: "Mina Kalas | PartyWhen – Gratis Barnkalas Planerare",
      description: "Visa och hantera alla dina barnkalas. Kopiera tidigare event, följ OSA och få tillgång till dina admin-dashboards.",
    },
    guest: {
      title: "Du är Inbjuden! | PartyWhen",
      description: "Du har blivit inbjuden till ett kalas! Rösta på datum, OSA och reservera presenter från önskelistan.",
    },
    admin: {
      title: "Kalas Dashboard | PartyWhen – Hantera Ditt Kalas",
      description: "Hantera ditt kalas med realtids-OSA, datumröstningsresultat, gästernas kostönskemål och mer.",
    },
    thanks: {
      title: "Tackkort | PartyWhen",
      description: "Skapa och dela vackra tackkort till dina kalasgäster.",
    },
    policy: {
      title: "Cookiepolicy & Ansvarsfriskrivning | PartyWhen",
      description: "Läs vår cookiepolicy och ansvarsfriskrivning. Vi använder endast nödvändiga cookies och tar inget ansvar för användargenererat innehåll.",
    },
  },
};

const faqDataSv = [
  { q: "Är PartyWhen gratis?", a: "Ja, PartyWhen är helt gratis att använda med alla funktioner inkluderade." },
  { q: "Kan gäster rösta på kalasdatum?", a: "Ja, dela gästlänken så kan gästerna rösta på vilka datum som passar dem bäst." },
  { q: "Behöver jag skapa ett konto?", a: "Nej, du kan skapa ett kalas utan konto. Men loggar du in sparas dina kalas så du kan komma åt dem igen." },
  { q: "Fungerar appen på svenska?", a: "Ja, appen stöder både svenska och engelska." },
  { q: "Behöver gästerna skapa konto?", a: "Nej, gästerna klickar bara på länken du delar. Inget konto eller app behövs." },
  { q: "Hur fungerar önskelistan?", a: "Du lägger in presenter. Gästerna ser listan och kan hemligt reservera en present — så andra inte köper samma sak." },
  { q: "Kan jag bjuda in via SMS eller WhatsApp?", a: "Ja, du kan skicka inbjudan direkt via SMS, WhatsApp eller Messenger med ett klick." },
  { q: "Kan flera vuxna administrera kalaset?", a: "Ja, du kan lägga till medadministratörer som får egna hemliga länkar till dashboarden." },
];

const faqDataEn = [
  { q: "Is PartyWhen free?", a: "Yes, completely free with all features included. No hidden costs." },
  { q: "Can guests vote on dates?", a: "Yes! Propose 2-3 dates and guests vote Yes, Maybe, or No. See results in real-time on your dashboard." },
  { q: "Do guests need to create an account?", a: "No, guests just click the link you share. No account or app download needed." },
  { q: "How does the wishlist work?", a: "You add gift ideas. Guests see the list and can secretly claim an item — so others won't buy the same thing." },
  { q: "Can I invite via SMS or WhatsApp?", a: "Yes, send invitations directly via SMS, WhatsApp, or Messenger with one click." },
  { q: "Does it work on mobile?", a: "Absolutely! The app is fully responsive for both planners and guests." },
  { q: "Can multiple people manage the party?", a: "Yes, you can add co-admins who get their own secret links to the dashboard." },
  { q: "What happens to my data?", a: "Your data is stored securely. Sign in to access your parties again. Without signing in, save your admin links." },
];

const SEOHead = ({ page = 'home', partyTitle, childName, playCenterName, playCenterDescription }: SEOHeadProps) => {
  const { language } = useLanguage();
  const location = useLocation();
  
  useEffect(() => {
    let title: string;
    let description: string;

    if (page === 'play-center' && playCenterName) {
      title = language === 'sv'
        ? `Barnkalas på ${playCenterName} | PartyWhen`
        : `Kids Party at ${playCenterName} | PartyWhen`;
      description = playCenterDescription || (language === 'sv'
        ? `Planera barnkalas på ${playCenterName}. Skicka inbjudan, samla OSA och låt gästerna rösta på datum – gratis!`
        : `Plan a kids party at ${playCenterName}. Send invitations, collect RSVPs, and let guests vote on dates – free!`);
    } else {
      const data = seoData[language][page === 'play-center' ? 'home' : page];
      title = data.title;
      description = data.description;
      
      if (partyTitle && (page === 'guest' || page === 'admin' || page === 'thanks')) {
        title = `${partyTitle} | PartyWhen`;
        if (childName) {
          description = language === 'sv' 
            ? `${childName}s kalas - ${data.description}`
            : `${childName}'s party - ${data.description}`;
        }
      }
    }
    
    // Update document title
    document.title = title;
    
    // Update meta description
    updateMeta('name', 'description', description);
    
    // Canonical - current language version
    const currentPath = location.pathname;
    const canonicalUrl = `${BASE_URL}${currentPath}`;
    updateLink('canonical', canonicalUrl);
    
    // Hreflang tags
    const pathWithoutLang = currentPath.replace(/^\/(sv|en)/, '');
    updateHreflang('sv-SE', `${BASE_URL}/sv${pathWithoutLang || '/'}`);
    updateHreflang('en', `${BASE_URL}/en${pathWithoutLang || '/'}`);
    updateHreflang('x-default', `${BASE_URL}/sv${pathWithoutLang || '/'}`);
    
    // Open Graph tags
    updateMeta('property', 'og:title', title);
    updateMeta('property', 'og:description', description);
    updateMeta('property', 'og:url', canonicalUrl);
    updateMeta('property', 'og:locale', language === 'sv' ? 'sv_SE' : 'en_US');
    updateMeta('property', 'og:locale:alternate', language === 'sv' ? 'en_US' : 'sv_SE');
    
    // Twitter tags
    updateMeta('name', 'twitter:title', title);
    updateMeta('name', 'twitter:description', description);
    
    // Update HTML lang attribute
    document.documentElement.lang = language === 'sv' ? 'sv' : 'en';
    
    // Dynamic JSON-LD for home page
    if (page === 'home') {
      injectJsonLd('seo-webapp-jsonld', getWebAppSchema(language));
      injectJsonLd('seo-faq-jsonld', getFaqSchema(language));
    } else {
      removeJsonLd('seo-webapp-jsonld');
      removeJsonLd('seo-faq-jsonld');
    }
    
  }, [language, page, partyTitle, childName, playCenterName, playCenterDescription, location.pathname]);
  
  return null;
};

function updateMeta(attr: 'name' | 'property', key: string, content: string) {
  let el = document.querySelector(`meta[${attr}="${key}"]`);
  if (!el) {
    el = document.createElement('meta');
    el.setAttribute(attr, key);
    document.head.appendChild(el);
  }
  el.setAttribute('content', content);
}

function updateLink(rel: string, href: string) {
  let el = document.querySelector(`link[rel="${rel}"]`) as HTMLLinkElement;
  if (!el) {
    el = document.createElement('link');
    el.rel = rel;
    document.head.appendChild(el);
  }
  el.href = href;
}

function updateHreflang(hreflang: string, href: string) {
  let el = document.querySelector(`link[hreflang="${hreflang}"]`) as HTMLLinkElement;
  if (!el) {
    el = document.createElement('link');
    el.rel = 'alternate';
    el.setAttribute('hreflang', hreflang);
    document.head.appendChild(el);
  }
  el.href = href;
}

function injectJsonLd(id: string, data: object) {
  let el = document.getElementById(id);
  if (!el) {
    el = document.createElement('script');
    el.id = id;
    el.setAttribute('type', 'application/ld+json');
    document.head.appendChild(el);
  }
  el.textContent = JSON.stringify(data);
}

function removeJsonLd(id: string) {
  const el = document.getElementById(id);
  if (el) el.remove();
}

function getWebAppSchema(lang: string) {
  return {
    "@context": "https://schema.org",
    "@type": "WebApplication",
    "name": "PartyWhen",
    "alternateName": "PartyWhen",
    "url": `${BASE_URL}/${lang}/`,
    "description": lang === 'sv'
      ? "Planera barnkalas utan stress. Datumröstning, digitala inbjudningar, gästhantering, önskelistor och aktivitetsplanering."
      : "Plan kids birthday parties stress-free. Date voting, digital invitations, guest management, wishlists and activity planning.",
    "applicationCategory": "LifestyleApplication",
    "operatingSystem": "Web",
    "offers": {
      "@type": "Offer",
      "price": "0",
      "priceCurrency": "SEK"
    },
    "inLanguage": [lang],
    "audience": {
      "@type": "Audience",
      "audienceType": "Parents planning children's parties"
    },
    "featureList": lang === 'sv' ? [
      "Datumröstning för gäster",
      "Digitala inbjudningar med mallar",
      "Gästhantering och OSA",
      "Önskelista med presentreservation",
      "Aktivitetsplanering",
      "Inköpslista",
      "Budgetöversikt"
    ] : [
      "Date voting for guests",
      "Digital invitations with templates",
      "Guest management and RSVP",
      "Wishlist with gift claiming",
      "Activity planning",
      "Shopping list",
      "Budget overview"
    ]
  };
}

function getFaqSchema(lang: string) {
  const faqs = lang === 'sv' ? faqDataSv : faqDataEn;
  return {
    "@context": "https://schema.org",
    "@type": "FAQPage",
    "mainEntity": faqs.map(f => ({
      "@type": "Question",
      "name": f.q,
      "acceptedAnswer": {
        "@type": "Answer",
        "text": f.a
      }
    }))
  };
}

export default SEOHead;
